import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import useNewsletterSubscriptionModalFields from './useNewsletterSubscriptionModalFields'
import Field from '../field'
import PhoneField from '../phone-field'

/**
 * Renders a set of input fields for a newsletter subscription modal, including email, phone, first name, and last name.
 * Optionally focuses on the first input field when the component is mounted.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.form - The form object containing field configurations.
 * @param {string} [props.prefix=''] - An optional prefix to be applied to field names.
 * @param {boolean} props.isFirstInputFocus - Determines if the first input field should be focused on mount.
 *
 * @returns {JSX.Element} A stack of input fields for newsletter subscription.
 */
const NewsletterSubscriptionModalFields = ({form, prefix = '', isFirstInputFocus}) => {
    const fields = useNewsletterSubscriptionModalFields({form, prefix})
    const emailFieldRef = useRef()

    useEffect(() => {
        isFirstInputFocus && emailFieldRef?.current?.focus()
    }, [])

    return (
        <Stack spacing={10}>
            <Field {...fields.email} inputRef={emailFieldRef} />
            <PhoneField {...fields.phone} />
            <Field {...fields.firstName} hasIndicator={false} />
            <Field {...fields.lastName} hasIndicator={false} />
        </Stack>
    )
}

NewsletterSubscriptionModalFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,

    /** Determines if the first input should be focused */
    isFirstInputFocus: PropTypes.bool
}

export default NewsletterSubscriptionModalFields
